import { useState } from "react";
import { TriangleAlertIcon, CircleCheckIcon } from "lucide-react";
import { api } from "@/client/lib";
import { Alert, AlertDescription, AlertTitle } from "@/client/components/base/alert";
import { Button } from "@/client/components/base/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/client/components/base/card";
import PasswordInput from "@/client/components/derived/PasswordInput";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/client/components/base/field";
import { Input } from "@/client/components/base/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/client/components/base/select";
import { Spinner } from "@/client/components/base/spinner";
import { toast } from "@/client/components/base/toast";

const BROKER_ITEMS = [
    { label: "Select a broker", value: null },
    { label: "Zerodha", value: "zerodha" },
];

const REQUIRED_FIELDS = [
    "username",
    "broker",
    "brokerUserId",
    "password",
    "brokerPassword",
    "telegramChatId",
    "apiKey",
    "apiSecret",
] as const;

type RequiredField = (typeof REQUIRED_FIELDS)[number];

type FormState = {
    username: string;
    broker: string;
    brokerUserId: string;
    password: string;
    brokerPassword: string;
    telegramChatId: string;
    apiKey: string;
    apiSecret: string;
    totpSecret: string;
};

const EMPTY_FORM: FormState = {
    username: "",
    broker: "",
    brokerUserId: "",
    password: "",
    brokerPassword: "",
    telegramChatId: "",
    apiKey: "",
    apiSecret: "",
    totpSecret: "",
};

const FIELD_LABELS: Record<RequiredField, string> = {
    username: "Username",
    broker: "Broker",
    brokerUserId: "Broker user ID",
    password: "User password",
    brokerPassword: "Broker password",
    telegramChatId: "Telegram chat ID",
    apiKey: "API key",
    apiSecret: "API secret",
};

const RegisterPage = () => {
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [errors, setErrors] = useState<Partial<Record<RequiredField, string>>>({});
    const [formError, setFormError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRegistered, setIsRegistered] = useState(false);

    const isComplete = REQUIRED_FIELDS.every((field) => form[field].trim().length > 0);

    const setField =
        (field: keyof FormState) =>
        (value: string) => {
            setForm((current) => ({ ...current, [field]: value }));
            setErrors((current) => ({ ...current, [field]: undefined }));
            setFormError(null);
        };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const newErrors: Partial<Record<RequiredField, string>> = {};
        for (const field of REQUIRED_FIELDS) {
            if (form[field].trim().length === 0) {
                newErrors[field] = `${FIELD_LABELS[field]} is required`;
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);
        setFormError(null);

        const { error } = await api.users.register.post({
            username: form.username.trim(),
            broker: form.broker,
            brokerUserId: form.brokerUserId.trim(),
            password: form.password,
            brokerPassword: form.brokerPassword,
            telegramChatId: form.telegramChatId.trim(),
            apiKey: form.apiKey.trim(),
            apiSecret: form.apiSecret.trim(),
            totpSecret: form.totpSecret.trim() || undefined,
        });

        setIsSubmitting(false);

        if (error) {
            const message =
                error.value && typeof error.value === "object" && "message" in error.value
                    ? String(error.value.message)
                    : `Registration failed (${error.status})`;
            setFormError(message);
            return;
        }

        toast.add({
            title: "Account created",
            description: "Your credentials are encrypted and stored.",
            type: "success",
        });
        setIsRegistered(true);
        setForm(EMPTY_FORM);
    };

    return (
        <main className="relative flex min-h-svh items-center justify-center overflow-hidden bg-background p-4 sm:p-8">
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 [background:radial-gradient(70%_60%_at_50%_-10%,var(--primary)_0%,transparent_60%)] opacity-10"
            />
            <Card className="relative w-full max-w-5xl">
                <CardHeader>
                    <CardTitle>Create your account</CardTitle>
                    <CardDescription>
                        Register a new Alphagrid user. Broker and API credentials are encrypted at rest.
                    </CardDescription>
                </CardHeader>

                {formError && (
                    <div className="px-4 sm:px-6">
                        <Alert variant="destructive">
                            <TriangleAlertIcon aria-hidden="true" />
                            <AlertTitle>Registration failed</AlertTitle>
                            <AlertDescription>{formError}</AlertDescription>
                        </Alert>
                    </div>
                )}

                {isRegistered && (
                    <div className="px-4 sm:px-6">
                        <Alert>
                            <CircleCheckIcon aria-hidden="true" />
                            <AlertTitle>Account created</AlertTitle>
                            <AlertDescription>
                                Your account is ready. Register another account below or close this page.
                            </AlertDescription>
                        </Alert>
                    </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                    <CardContent>
                        <div className="grid items-start gap-4 md:grid-cols-3">
                            <Card size="sm">
                                <CardHeader>
                                    <CardTitle>Account</CardTitle>
                                    <CardDescription>Your login username and password.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <FieldGroup>
                                        <Field data-invalid={!!errors.username}>
                                            <FieldLabel htmlFor="username">Username</FieldLabel>
                                            <Input
                                                id="username"
                                                value={form.username}
                                                placeholder="e.g. alpha_trader"
                                                autoComplete="username"
                                                aria-invalid={!!errors.username}
                                                onChange={(event) => setField("username")(event.target.value)}
                                            />
                                            <FieldError
                                                errors={errors.username ? [{ message: errors.username }] : undefined}
                                            />
                                        </Field>
                                        <Field data-invalid={!!errors.password}>
                                            <FieldLabel htmlFor="password">User password</FieldLabel>
                                            <PasswordInput
                                                id="password"
                                                value={form.password}
                                                placeholder="Login password"
                                                invalid={!!errors.password}
                                                autoComplete="new-password"
                                                onChange={setField("password")}
                                            />
                                            <FieldError
                                                errors={errors.password ? [{ message: errors.password }] : undefined}
                                            />
                                        </Field>
                                        <Field data-invalid={!!errors.telegramChatId}>
                                            <FieldLabel htmlFor="telegramChatId">Telegram chat ID</FieldLabel>
                                            <Input
                                                id="telegramChatId"
                                                value={form.telegramChatId}
                                                placeholder="e.g. 123456789"
                                                autoComplete="off"
                                                aria-invalid={!!errors.telegramChatId}
                                                onChange={(event) => setField("telegramChatId")(event.target.value)}
                                            />
                                            <FieldError
                                                errors={
                                                    errors.telegramChatId ? [{ message: errors.telegramChatId }] : undefined
                                                }
                                            />
                                        </Field>
                                    </FieldGroup>
                                </CardContent>
                            </Card>

                            <Card size="sm">
                                <CardHeader>
                                    <CardTitle>Broker credentials</CardTitle>
                                    <CardDescription>Your broker trading account.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <FieldGroup>
                                        <Field data-invalid={!!errors.broker}>
                                            <FieldLabel htmlFor="broker">Broker</FieldLabel>
                                            <Select
                                                items={BROKER_ITEMS}
                                                value={form.broker || null}
                                                onValueChange={(value) => setField("broker")(value ?? "")}
                                            >
                                                <SelectTrigger id="broker" aria-invalid={!!errors.broker}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectGroup>
                                                        {BROKER_ITEMS.map((item) => (
                                                            <SelectItem key={item.value} value={item.value}>
                                                                {item.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectGroup>
                                                </SelectContent>
                                            </Select>
                                            <FieldError
                                                errors={errors.broker ? [{ message: errors.broker }] : undefined}
                                            />
                                        </Field>
                                        <Field data-invalid={!!errors.brokerUserId}>
                                            <FieldLabel htmlFor="brokerUserId">Broker user ID</FieldLabel>
                                            <Input
                                                id="brokerUserId"
                                                value={form.brokerUserId}
                                                placeholder="Zerodha account ID"
                                                autoComplete="off"
                                                aria-invalid={!!errors.brokerUserId}
                                                onChange={(event) => setField("brokerUserId")(event.target.value)}
                                            />
                                            <FieldError
                                                errors={errors.brokerUserId ? [{ message: errors.brokerUserId }] : undefined}
                                            />
                                        </Field>
                                        <Field data-invalid={!!errors.brokerPassword}>
                                            <FieldLabel htmlFor="brokerPassword">Broker password</FieldLabel>
                                            <PasswordInput
                                                id="brokerPassword"
                                                value={form.brokerPassword}
                                                placeholder="Zerodha account password"
                                                invalid={!!errors.brokerPassword}
                                                autoComplete="new-password"
                                                onChange={setField("brokerPassword")}
                                            />
                                            <FieldError
                                                errors={errors.brokerPassword ? [{ message: errors.brokerPassword }] : undefined}
                                            />
                                        </Field>
                                    </FieldGroup>
                                </CardContent>
                            </Card>

                            <Card size="sm">
                                <CardHeader>
                                    <CardTitle>API credentials</CardTitle>
                                    <CardDescription>Kite API keys, encrypted at rest.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <FieldGroup>
                                        <Field data-invalid={!!errors.apiKey}>
                                            <FieldLabel htmlFor="apiKey">API key</FieldLabel>
                                            <Input
                                                id="apiKey"
                                                value={form.apiKey}
                                                placeholder="Kite API key"
                                                autoComplete="off"
                                                aria-invalid={!!errors.apiKey}
                                                onChange={(event) => setField("apiKey")(event.target.value)}
                                            />
                                            <FieldError
                                                errors={errors.apiKey ? [{ message: errors.apiKey }] : undefined}
                                            />
                                        </Field>
                                        <Field data-invalid={!!errors.apiSecret}>
                                            <FieldLabel htmlFor="apiSecret">API secret</FieldLabel>
                                            <PasswordInput
                                                id="apiSecret"
                                                value={form.apiSecret}
                                                placeholder="Kite API secret"
                                                invalid={!!errors.apiSecret}
                                                autoComplete="off"
                                                onChange={setField("apiSecret")}
                                            />
                                            <FieldError
                                                errors={errors.apiSecret ? [{ message: errors.apiSecret }] : undefined}
                                            />
                                        </Field>
                                        <Field>
                                            <FieldLabel htmlFor="totpSecret">
                                                TOTP secret <span className="font-normal text-muted-foreground">optional</span>
                                            </FieldLabel>
                                            <PasswordInput
                                                id="totpSecret"
                                                value={form.totpSecret}
                                                placeholder="Base32 TOTP secret"
                                                autoComplete="off"
                                                onChange={setField("totpSecret")}
                                            />
                                        </Field>
                                    </FieldGroup>
                                </CardContent>
                            </Card>
                        </div>
                    </CardContent>

                    <CardFooter className="justify-center">
                        <Button
                            type="submit"
                            className="w-full max-w-sm"
                            disabled={!isComplete || isSubmitting}
                        >
                            {isSubmitting ? (
                                <Spinner data-icon="inline-start" />
                            ) : null}
                            {isSubmitting ? "Creating account..." : "Create account"}
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </main>
    );
};

export default RegisterPage;
